import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Switch } from './ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { toast } from 'sonner';
import { getTeamMembers, createTeamMember, updateTeamMember, deleteTeamMember } from '../services/api';
import { Loader2, Plus, Edit, Trash2, User, ArrowUp, ArrowDown } from 'lucide-react';

export const TeamMemberManager = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    bio: '',
    image: '',
    order: 0,
    enabled: true
  });

  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    try {
      const data = await getTeamMembers();
      setMembers(data);
    } catch (error) {
      console.error('Failed to load team members:', error);
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (member = null) => {
    if (member) {
      setEditingMember(member);
      setFormData({
        name: member.name,
        position: member.position,
        bio: member.bio,
        image: member.image,
        order: member.order,
        enabled: member.enabled
      });
    } else {
      setEditingMember(null);
      setFormData({
        name: '',
        position: '',
        bio: '',
        image: '',
        order: members.length,
        enabled: true
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingMember) {
        await updateTeamMember(editingMember.id, { ...editingMember, ...formData });
        toast.success('Team member updated successfully!');
      } else {
        await createTeamMember(formData);
        toast.success('Team member added successfully!');
      }
      setDialogOpen(false);
      loadMembers();
    } catch (error) {
      console.error('Failed to save team member:', error);
      toast.error('Failed to save team member');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this team member?')) {
      return;
    }
    
    try {
      await deleteTeamMember(id);
      toast.success('Team member deleted successfully!');
      loadMembers();
    } catch (error) {
      console.error('Failed to delete team member:', error);
      toast.error('Failed to delete team member');
    }
  };

  const moveUp = async (member, index) => {
    if (index === 0) return;
    
    const prevMember = members[index - 1];
    const updatedMember = { ...member, order: prevMember.order };
    const updatedPrevMember = { ...prevMember, order: member.order };
    
    try {
      await Promise.all([
        updateTeamMember(member.id, updatedMember),
        updateTeamMember(prevMember.id, updatedPrevMember)
      ]);
      loadMembers();
    } catch (error) {
      console.error('Failed to reorder:', error);
      toast.error('Failed to reorder team members');
    }
  };

  const moveDown = async (member, index) => {
    if (index === members.length - 1) return;
    
    const nextMember = members[index + 1];
    const updatedMember = { ...member, order: nextMember.order };
    const updatedNextMember = { ...nextMember, order: member.order };
    
    try {
      await Promise.all([
        updateTeamMember(member.id, updatedMember),
        updateTeamMember(nextMember.id, updatedNextMember)
      ]);
      loadMembers();
    } catch (error) {
      console.error('Failed to reorder:', error);
      toast.error('Failed to reorder team members');
    }
  };

  const toggleEnabled = async (member) => {
    try {
      await updateTeamMember(member.id, { ...member, enabled: !member.enabled });
      toast.success(`Team member ${member.enabled ? 'disabled' : 'enabled'} successfully!`);
      loadMembers();
    } catch (error) {
      console.error('Failed to toggle member:', error);
      toast.error('Failed to update team member');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-otter-green" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold dark:text-white">Team Members</h2>
          <p className="text-gray-600 dark:text-gray-400">Manage your team member profiles</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => handleOpenDialog()} className="bg-otter-green hover:bg-otter-green-600">
              <Plus className="mr-2 h-4 w-4" />
              Add Team Member
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl dark:bg-gray-800 dark:border-gray-700">
            <DialogHeader>
              <DialogTitle className="dark:text-white">
                {editingMember ? 'Edit Team Member' : 'Add Team Member'}
              </DialogTitle>
              <DialogDescription className="dark:text-gray-400">
                Fill in the team member details below
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="John Doe"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="position">Position *</Label>
                  <Input
                    id="position"
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    placeholder="Property Manager"
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="image">Image URL</Label>
                <Input
                  id="image"
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  placeholder="https://..."
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="bio">Bio</Label>
                <Textarea
                  id="bio"
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  placeholder="Brief description about the team member..."
                  rows={4}
                  className="mt-1"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="enabled"
                  checked={formData.enabled}
                  onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked })}
                />
                <Label htmlFor="enabled">Visible on website</Label>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={!formData.name || !formData.position}
                className="bg-otter-green hover:bg-otter-green-600"
              >
                {editingMember ? 'Update' : 'Add'} Team Member
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {members.length === 0 ? (
        <Card className="dark:bg-gray-800 dark:border-gray-700">
          <CardContent className="py-12 text-center">
            <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400 mb-4">No team members added yet</p>
            <Button onClick={() => handleOpenDialog()} variant="outline">
              <Plus className="mr-2 h-4 w-4" />
              Add Your First Team Member
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {members.map((member, index) => (
            <Card key={member.id} className="dark:bg-gray-800 dark:border-gray-700">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  {member.image && (
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-20 h-20 rounded-full object-cover"
                    />
                  )}
                  {!member.image && (
                    <div className="w-20 h-20 rounded-full bg-otter-green-100 dark:bg-otter-green-800/30 flex items-center justify-center">
                      <User className="h-10 w-10 text-otter-green dark:text-otter-green-400" />
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-lg font-semibold dark:text-white">{member.name}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{member.position}</p>
                        {member.bio && (
                          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">{member.bio}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => moveUp(member, index)}
                          disabled={index === 0}
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => moveDown(member, index)}
                          disabled={index === members.length - 1}
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                        <Switch
                          checked={member.enabled}
                          onCheckedChange={() => toggleEnabled(member)}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleOpenDialog(member)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(member.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
